import { useEffect } from "react";
import { createLogger } from "debug/DebugingLogger";
import {
  FromWorkerMessage,
  WorkerOperationMessage,
  MessageType,
  OperationType,
} from "Worker/entities";
import {
  showNotificationGlobal,
  updateNotificationProgressGlobal,
} from "stores/useNotificationStore";
import { ClientApp } from "target_wasm";
import { Status } from "components/common/types";
import { useSessionExpiry } from "contexts/SessionExpiryContext";

const log = createLogger("useWorkerOperation");
const STATUS_RESET_DELAY_MS = 3000;

/** Truncate a filename to keep notification text readable. */
export function truncateFilename(name: string, maxLen = 28): string {
  if (name.length <= maxLen) return name;
  const ext = name.lastIndexOf(".");
  if (ext > 0) {
    const suffix = name.slice(ext); // e.g. ".csv"
    const baseMax = maxLen - suffix.length - 1; // 1 for "…"
    if (baseMax > 4) return name.slice(0, baseMax) + "…" + suffix;
  }
  return name.slice(0, maxLen - 1) + "…";
}

export enum FileType {
  CSV = "csv",
  Parquet = "parquet",
  LocalSqlite = "local_sqlite",
}

export function detectFileType(filename: string): FileType | null {
  const lowerFilename = filename.toLowerCase();
  if (lowerFilename.endsWith(".csv")) {
    return FileType.CSV;
  }
  if (lowerFilename.endsWith(".parquet") || lowerFilename.endsWith(".pq")) {
    return FileType.Parquet;
  }
  if (
    lowerFilename.endsWith(".sqlite") ||
    lowerFilename.endsWith(".db") ||
    lowerFilename.endsWith(".sqlite3")
  ) {
    return FileType.LocalSqlite;
  }
  return null;
}

/** Canonical SQLite magic header — 16 bytes including the trailing
 *  null. Same string the server-side handler / WAL detector use. */
const SQLITE_MAGIC = new Uint8Array([
  0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74,
  0x20, 0x33, 0x00,
]);

/** Sniff the first 16 bytes of a `Blob` for the canonical
 *  `SQLite format 3\0` magic. Used by the dropzone routing to
 *  distinguish a renamed-to-.csv SQLite file from a real CSV, and to
 *  confirm a `.db` file really is SQLite (not, say, a Berkeley DB or
 *  a SQLite-looking misnamed binary). */
export async function hasSqliteMagic(blob: Blob): Promise<boolean> {
  if (blob.size < SQLITE_MAGIC.length) return false;
  const head = await blob.slice(0, SQLITE_MAGIC.length).arrayBuffer();
  const view = new Uint8Array(head);
  for (let i = 0; i < SQLITE_MAGIC.length; i++) {
    if (view[i] !== SQLITE_MAGIC[i]) return false;
  }
  return true;
}

/** Result of {@link detectFileTypeFromFile} — combines the extension
 *  check with a magic-header sniff so the dropzone can route to the
 *  right flow AND surface a "looks like SQLite, rename to .sqlite?"
 *  hint for the surprising case of a .csv with SQLite magic. */
export type DetectFileTypeFromFileResult =
  /** Standard route — use {@link FileType.CSV} / Parquet / LocalSqlite
   *  via the normal handler. */
  | { kind: "route"; fileType: FileType }
  /** Extension is .csv (or any non-SQLite) but the bytes look like a
   *  SQLite database. UI surfaces the rename hint; the file is NOT
   *  imported as CSV — it would explode at parse time anyway. */
  | { kind: "csv-with-sqlite-magic"; filename: string }
  /** Extension claims `.sqlite`/`.db`/`.sqlite3` but the file's not
   *  actually SQLite. Reject with the "not a SQLite file" message. */
  | { kind: "sqlite-extension-without-magic"; filename: string }
  /** Extension we don't handle and no special-case hint applies. */
  | { kind: "unsupported"; filename: string };

/**
 * Extension-first routing with a magic-header tiebreaker on the
 * SQLite cases. Used by the dropzone (T14) to choose between CSV /
 * Parquet / Local SQLite imports without trusting the extension
 * alone.
 *
 * Rules:
 *  - Extension says CSV/Parquet → check magic. If SQLite magic
 *    appears, surface the rename hint (the file would fail CSV
 *    parsing anyway). Otherwise route to the extension's flow.
 *  - Extension says SQLite → check magic. Confirmed → LocalSqlite.
 *    Missing → "sqlite-extension-without-magic" with the
 *    NOT_SQLITE_ERROR hint.
 *  - Extension we don't handle → "unsupported".
 */
export async function detectFileTypeFromFile(
  file: File,
): Promise<DetectFileTypeFromFileResult> {
  const ext = detectFileType(file.name);
  const magic = await hasSqliteMagic(file);

  if (ext === FileType.LocalSqlite) {
    return magic
      ? { kind: "route", fileType: FileType.LocalSqlite }
      : { kind: "sqlite-extension-without-magic", filename: file.name };
  }
  if (ext === FileType.CSV || ext === FileType.Parquet) {
    return magic
      ? { kind: "csv-with-sqlite-magic", filename: file.name }
      : { kind: "route", fileType: ext };
  }
  // Extension we don't claim. Magic header doesn't promote — we don't
  // import unnamed-extension SQLite files, the user has to be explicit.
  return { kind: "unsupported", filename: file.name };
}

export function createFileLoadHandler(clientApp: ClientApp | null) {
  return (_filename: string, tableID?: string) => {
    if (!tableID || !clientApp) {
      return;
    }
    try {
      // After the FileUpload retirement, uploaded tables are tagged Owned —
      // there is no upstream source to remember. The auto-checkpoint on
      // import preserves the data in regular history.
      clientApp.handle_file_load(tableID);
    } catch (error: unknown) {
      log.error("Error handling file load", error);
      showNotificationGlobal("Failed to upload file", "error");
    }
  };
}

export interface useWorkerOperationOptions<
  TSuccessParams extends unknown[] = [string],
> {
  worker: Worker | null;
  operation: OperationType;
  onSuccess?: (filename: string, ...args: TSuccessParams) => void;
  onError?: (filename: string, message: string) => void;
  setLoadingStatus?: (status: Status) => void;
  trackActiveOperations?: boolean;
}

function getOperation(
  operation: OperationType,
  progressive: boolean = false,
): string {
  const base = operation === OperationType.Upload ? "Upload" : "Download";
  return progressive ? `${base}ing` : base;
}

function handleOperationResult(
  filename: string,
  isSuccess: boolean,
  operation: OperationType,
  onSuccess?: (filename: string, ...args: unknown[]) => void,
  onError?: (filename: string, message: string) => void,
  setLoadingStatus?: (status: Status) => void,
  message?: string,
  tableID?: string,
  modelID?: string,
): void {
  const op = getOperation(operation);

  if (isSuccess) {
    setLoadingStatus?.(Status.Success);
    if (operation === OperationType.Download) {
      showNotificationGlobal(
        `${op}ed ${truncateFilename(filename)} successfully`,
        "default",
      );
    }
    if (onSuccess) {
      if (operation === OperationType.Upload && tableID) {
        onSuccess(filename, tableID, modelID);
      } else {
        onSuccess(filename);
      }
    }
  } else {
    setLoadingStatus?.(Status.Failure);
    const fallback = `${op} failed`;
    // Surface the actual reason (e.g. "unsupported zstd compression") instead
    // of a bare "Upload failed". The notification store's
    // sanitizeErrorForDisplay() strips HTTP/server noise; falls back to the
    // generic phrase when the worker gave no detail.
    showNotificationGlobal(message || fallback, "error");
    onError?.(filename, message || fallback);
  }

  if (setLoadingStatus) {
    setTimeout(() => setLoadingStatus(Status.Idle), STATUS_RESET_DELAY_MS);
  }
}

function showProgressSnackbar(
  filename: string,
  operation: OperationType,
  progress: number,
  activeOperations?: Map<string, boolean>,
): void {
  if (activeOperations && !activeOperations.get(filename)) {
    activeOperations.set(filename, true);
    const op = getOperation(operation, true);
    showNotificationGlobal(
      `${op} ${truncateFilename(filename)}...`,
      "default",
      undefined,
      progress,
    );
  }
}

export function useWorkerOperation<
  TSuccessParams extends unknown[] = [string],
>({
  worker,
  operation,
  onSuccess,
  onError,
  setLoadingStatus,
  trackActiveOperations = operation === OperationType.Download,
}: useWorkerOperationOptions<TSuccessParams>): void {
  const { isShowingDialog } = useSessionExpiry();
  useEffect(() => {
    if (!worker) return;

    const activeOperations = trackActiveOperations
      ? new Map<string, boolean>()
      : undefined;

    const handleMessage = (event: MessageEvent<FromWorkerMessage>) => {
      const message = event.data;

      if (!("operation" in message) || message.operation !== operation) {
        return;
      }

      const opMessage = message as WorkerOperationMessage;

      switch (opMessage.type) {
        case MessageType.Progress: {
          updateNotificationProgressGlobal(opMessage.progress);
          if (trackActiveOperations) {
            showProgressSnackbar(
              opMessage.filename,
              operation,
              opMessage.progress,
              activeOperations,
            );
          }
          break;
        }

        case MessageType.Status: {
          activeOperations?.delete(opMessage.filename);
          handleOperationResult(
            opMessage.filename,
            opMessage.success,
            operation,
            onSuccess as
              ((filename: string, ...args: unknown[]) => void) | undefined,
            onError,
            setLoadingStatus,
            opMessage.message,
            opMessage.tableID,
            opMessage.modelID,
          );
          break;
        }

        case MessageType.Error: {
          if (isShowingDialog) {
            activeOperations?.delete(opMessage.filename);
            break;
          }
          activeOperations?.delete(opMessage.filename);
          handleOperationResult(
            opMessage.filename,
            false,
            operation,
            onSuccess as
              ((filename: string, ...args: unknown[]) => void) | undefined,
            onError,
            setLoadingStatus,
            opMessage.message,
          );
          break;
        }
      }
    };

    worker.addEventListener("message", handleMessage);
    return () => {
      setLoadingStatus?.(Status.Idle);
      worker.removeEventListener("message", handleMessage);
    };
  }, [
    worker,
    operation,
    onSuccess,
    onError,
    setLoadingStatus,
    trackActiveOperations,
    isShowingDialog,
  ]);
}
